/**
 * Module-launch hook for the sidebar — the `persistModuleLastOpened` /
 * `ensureModulePanelAttached` / `handleOpenSidebarModule` useCallback trio.
 *
 * The open callback crosses routing, active-conversation, snapshot fetch,
 * workspace API, query cache, a CustomEvent, and `onCloseSidebar`.
 *
 * Hook-order safety: the three callbacks must occupy a contiguous slot to
 * preserve React hook order. The 1200 ms recency-dedupe ref
 * (`lastModuleRecencyPersistRef`) is NOT recreated here — it stays in the
 * orchestrator (its position in the refs block must not move) and is threaded
 * in.
 */

import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query/keys';
import type { Conversation } from '@/types/conversations';
import { buildConversationPath } from '@/lib/workspace-routing';
import type { SidebarModuleItem } from '../types';

interface UseModuleLaunchArgs {
  token: string | null | undefined;
  workspaceId: string;
  queryClient: QueryClient;
  activeConversation: Conversation | null;
  orderedUserConversations: Conversation[];
  routeConversationId: string | null;
  setActiveConversation: (conversation: Conversation | null, options?: { skipFetch?: boolean }) => void;
  navigate: ReturnType<typeof useNavigate>;
  onCloseSidebar?: () => void;
  lastModuleRecencyPersistRef: MutableRefObject<{ moduleId: string; timestamp: number } | null>;
  /**
   * ModulePaneContext.openPane (ADR 028) — records the user's open intent in
   * the pane authority BEFORE navigating, so MainLayout's URL guard holds for
   * a not-yet-attached module instead of bouncing the click away.
   */
  openPane: (moduleId: string) => void;
}

export function useModuleLaunch({
  token,
  workspaceId,
  queryClient,
  activeConversation,
  orderedUserConversations,
  routeConversationId,
  setActiveConversation,
  navigate,
  onCloseSidebar,
  lastModuleRecencyPersistRef,
  openPane,
}: UseModuleLaunchArgs) {
  const persistModuleLastOpened = useCallback(async (moduleId: string, timestamp?: string) => {
    if (!moduleId || !token || !workspaceId) return;

    const nowMs = Date.now();
    const recentPersist = lastModuleRecencyPersistRef.current;
    if (recentPersist && recentPersist.moduleId === moduleId && nowMs - recentPersist.timestamp < 1200) {
      return;
    }

    lastModuleRecencyPersistRef.current = { moduleId, timestamp: nowMs };
    const lastOpenedAt = timestamp ?? new Date(nowMs).toISOString();

    // Optimistic update: immediately update local cache so the sidebar list stays stable
    const prefKey = queryKeys.modules.sidebarPreferences(workspaceId);
    queryClient.setQueryData(prefKey, (old: any[] | undefined) => {
      if (!old) return old;
      const exists = old.some((p: any) => p.moduleId === moduleId);
      if (exists) {
        return old.map((p: any) => p.moduleId === moduleId ? { ...p, lastOpenedAt } : p);
      }
      return [...old, { moduleId, lastOpenedAt }];
    });

    try {
      await api.workspaces.updateModuleSidebarPreference(token, workspaceId, moduleId, { lastOpenedAt });
      queryClient.invalidateQueries({ queryKey: prefKey });
    } catch (error) {
      console.warn('[ConversationSidebar] Failed to persist module lastOpenedAt', { moduleId, error });
      queryClient.invalidateQueries({ queryKey: prefKey });
    }
  }, [queryClient, token, workspaceId]);

  const ensureModulePanelAttached = useCallback(async (
    conversationId: string,
    moduleId: string,
  ): Promise<boolean> => {
    if (!token || !workspaceId) return false;

    try {
      const panels = await api.workspaces.listConversationPanels(
        token,
        workspaceId,
        conversationId,
        { includeArchived: true },
      );
      const activePanel = panels.find((panel) => !panel.archivedAt && panel.moduleId === moduleId);
      if (activePanel) return true;

      const existingPanel = panels.find((panel) => panel.moduleId === moduleId);
      if (existingPanel) {
        await api.workspaces.updateConversationPanel(
          token,
          workspaceId,
          conversationId,
          existingPanel.id,
          {
            archived: false,
            sortOrder: existingPanel.sortOrder,
          },
        );
        return true;
      }

      const maxSortOrder = panels.reduce((max, panel) => (
        typeof panel.sortOrder === 'number' && panel.sortOrder > max
          ? panel.sortOrder
          : max
      ), -1);

      await api.workspaces.createConversationPanel(
        token,
        workspaceId,
        conversationId,
        {
          workspaceModuleId: moduleId,
          panelKey: `module:${moduleId}`,
          sortOrder: maxSortOrder + 1,
        },
      );

      return true;
    } catch (error) {
      console.warn('[ConversationSidebar] Failed to ensure module panel attachment', {
        conversationId,
        moduleId,
        error,
      });
      return false;
    }
  }, [token, workspaceId]);

  const handleOpenSidebarModule = useCallback(async (item: SidebarModuleItem) => {
    const targetConversationId = routeConversationId
      ?? activeConversation?.id
      ?? orderedUserConversations[0]?.id
      ?? null;
    if (!targetConversationId) {
      return;
    }
    const targetConversation = activeConversation?.id === targetConversationId
      ? activeConversation
      : orderedUserConversations.find((conversation) => conversation.id === targetConversationId) ?? null;
    const isSameConversation = activeConversation?.id === targetConversationId;

    // SSOT (ADR 028): record the user's intent in the pane authority FIRST —
    // synchronous, so the URL guard's hold engages and a background hydration
    // read can never overwrite this click.
    openPane(item.module.id);

    // OPTIMISTIC: navigate + focus immediately — the pane open/switch must
    // never wait on network (measured: the old await-attach-then-snapshot
    // gate cost ~1.2s of dead time before the pane state even flipped, while
    // the server answered each call in ~12-50ms).
    navigate(buildConversationPath(workspaceId, targetConversationId, { moduleId: item.module.id }));
    if (isSameConversation) {
      // Force ChatBox to re-focus the module even if the route didn't change
      // (handles case where module pane was hidden by validation but URL is
      // the same). ChatBox's module.focus path OWNS attach + refresh when the
      // module isn't bound yet (ensureModulePanelInConversationOnce, deduped)
      // — attaching here too was pure duplication and a double-create race.
      // Same-conversation only: the listener reads the CURRENT conversation,
      // so dispatching across a not-yet-committed navigation would focus the
      // module on the wrong conversation.
      window.dispatchEvent(new CustomEvent('xavior:force-module-focus', {
        detail: { moduleId: item.module.id },
      }));
    } else {
      // Conversation switch: the URL hydration path seeds the pane from the
      // /mod/ param. Attach the panel, then delegate the conversation refresh to
      // ConversationContext — the SINGLE fetcher/writer of activeConversation. It
      // re-fetches conversations.detail(id) internally (the snapshot reflects the
      // freshly-attached panel because attach is awaited first, and it populates
      // the cache App's readiness gate reads) under its monotonic switch-sequence
      // guard. This replaces the sidebar's own un-cached, unguarded snapshot
      // fetch+write (and its dual fallback) with one delegated call — no second
      // uncoordinated writer of activeConversation.
      void (async () => {
        const attached = await ensureModulePanelAttached(targetConversationId, item.module.id);
        if (!attached) return;
        // Always apply the TARGET conversation — the list object when present,
        // else a minimal stub (a not-yet-listed deep link must not be stranded;
        // ConversationContext's detail fetch backfills the name + modules).
        setActiveConversation(targetConversation ?? { id: targetConversationId, name: '', modules: [] });
      })();
    }
    void persistModuleLastOpened(item.module.id);
    onCloseSidebar?.();
  }, [
    activeConversation,
    ensureModulePanelAttached,
    navigate,
    onCloseSidebar,
    openPane,
    orderedUserConversations,
    persistModuleLastOpened,
    routeConversationId,
    setActiveConversation,
    token,
    workspaceId,
  ]);

  return { handleOpenSidebarModule };
}
